// WorkflowDesignerCanvas.jsx

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  WORKFLOW_OUTCOME_COMPLETE,
  assignWorkflowSpineCanvas,
  getWorkflowIncomingEdges,
  listWorkflowTaskOutcomeKeys,
  normalizeWorkflowEdges,
  normalizeWorkflowOutcomes,
  removeWorkflowTaskFromGraph,
  validateWorkflowGraph,
} from 'workflows/workflowGraphUtils';

// CSS
import './WorkflowDesignerCanvas.css';

// Components
import Button from 'components/button/Button';
import WorkflowDesignerTaskNode, {
  WORKFLOW_DESIGNER_SOURCE_HANDLE,
} from './WorkflowDesignerTaskNode';

const nodeTypes = {
  workflowTask: WorkflowDesignerTaskNode,
};

function getOutcomeLabel(task, outcomeKey) {
  if (outcomeKey === WORKFLOW_OUTCOME_COMPLETE) return 'Complete';
  const customs = normalizeWorkflowOutcomes(task?.outcomes, { handledBy: task?.handledBy });
  return customs.find((entry) => entry.key === outcomeKey)?.label || outcomeKey;
}

function getFlowEdgeOutcomeKey(edge) {
  const fromData = String(edge?.data?.outcomeKey || '').trim();
  if (fromData) return fromData;
  // Legacy canvases used outcomeKey as sourceHandle.
  const fromHandle = String(edge?.sourceHandle || '').trim();
  if (fromHandle && fromHandle !== WORKFLOW_DESIGNER_SOURCE_HANDLE) {
    return fromHandle;
  }
  return WORKFLOW_OUTCOME_COMPLETE;
}

function buildTopologicalTaskKeys(tasks, edges) {
  const keys = (Array.isArray(tasks) ? tasks : [])
    .map((task) => String(task?.key || '').trim())
    .filter(Boolean);
  const keySet = new Set(keys);
  const inDegree = new Map(keys.map((key) => [key, 0]));
  const adjacency = new Map(keys.map((key) => [key, []]));

  normalizeWorkflowEdges(edges).forEach((edge) => {
    if (!edge.toTaskKey || !keySet.has(edge.fromTaskKey) || !keySet.has(edge.toTaskKey)) return;
    adjacency.get(edge.fromTaskKey).push(edge.toTaskKey);
    inDegree.set(edge.toTaskKey, (inDegree.get(edge.toTaskKey) || 0) + 1);
  });

  const queue = keys.filter((key) => (inDegree.get(key) || 0) === 0);
  const order = [];
  while (queue.length > 0) {
    const current = queue.shift();
    order.push(current);
    (adjacency.get(current) || []).forEach((nextKey) => {
      const next = (inDegree.get(nextKey) || 0) - 1;
      inDegree.set(nextKey, next);
      if (next === 0) queue.push(nextKey);
    });
  }

  keys.forEach((key) => {
    if (!order.includes(key)) order.push(key);
  });
  return order;
}

function buildFlowNodes(tasks, edges, handlers) {
  const incomingByTask = new Map();

  (Array.isArray(tasks) ? tasks : []).forEach((task) => {
    const key = String(task?.key || '').trim();
    if (!key) return;
    incomingByTask.set(key, getWorkflowIncomingEdges(edges, key).length);
  });

  return (Array.isArray(tasks) ? tasks : [])
    .map((task, index) => {
      const key = String(task?.key || '').trim();
      if (!key) return null;
      const canvas = task?.canvas && typeof task.canvas === 'object' ? task.canvas : null;
      return {
        id: key,
        type: 'workflowTask',
        position: {
          x: Number.isFinite(Number(canvas?.x)) ? Number(canvas.x) : (80 + (index * 280)),
          y: Number.isFinite(Number(canvas?.y)) ? Number(canvas.y) : 140,
        },
        data: {
          isStart: (incomingByTask.get(key) || 0) === 0,
          onAddOutcome: handlers?.onAddOutcome,
          onEditTask: handlers?.onEditTask,
          task,
        },
      };
    })
    .filter(Boolean);
}

function buildFlowEdges(edges, tasks) {
  const taskByKey = new Map(
    (Array.isArray(tasks) ? tasks : []).map((task) => [String(task?.key || '').trim(), task]),
  );

  return normalizeWorkflowEdges(edges)
    .filter((edge) => edge.toTaskKey != null)
    .map((edge) => {
      const isComplete = edge.outcomeKey === WORKFLOW_OUTCOME_COMPLETE;
      return {
        id: edge.key,
        source: edge.fromTaskKey,
        sourceHandle: WORKFLOW_DESIGNER_SOURCE_HANDLE,
        target: edge.toTaskKey,
        label: getOutcomeLabel(taskByKey.get(edge.fromTaskKey), edge.outcomeKey),
        data: { outcomeKey: edge.outcomeKey },
        type: 'default',
        // Complete is the primary spine — deleting it orphans steps and breaks the graph.
        deletable: !isComplete,
      };
    });
}

function flowEdgesToWorkflowEdges(flowEdges, previousEdges, tasks) {
  const next = [];
  const seen = new Set();

  (Array.isArray(flowEdges) ? flowEdges : []).forEach((edge) => {
    const fromTaskKey = String(edge?.source || '').trim();
    const outcomeKey = getFlowEdgeOutcomeKey(edge);
    const toTaskKey = String(edge?.target || '').trim() || null;
    if (!fromTaskKey || !toTaskKey) return;
    const dedupe = `${fromTaskKey}\u0001${outcomeKey}`;
    if (seen.has(dedupe)) return;
    seen.add(dedupe);
    next.push({
      key: String(edge?.id || '').trim() || `edge_${fromTaskKey}_${outcomeKey}_${toTaskKey}`,
      fromTaskKey,
      outcomeKey,
      toTaskKey,
    });
  });

  normalizeWorkflowEdges(previousEdges)
    .filter((edge) => edge.toTaskKey == null)
    .forEach((edge) => {
      const dedupe = `${edge.fromTaskKey}\u0001${edge.outcomeKey}`;
      if (seen.has(dedupe)) return;
      const task = (Array.isArray(tasks) ? tasks : []).find((row) => row.key === edge.fromTaskKey);
      if (!task) return;
      if (!listWorkflowTaskOutcomeKeys(task).includes(edge.outcomeKey)) return;
      seen.add(dedupe);
      next.push(edge);
    });

  (Array.isArray(tasks) ? tasks : []).forEach((task) => {
    listWorkflowTaskOutcomeKeys(task).forEach((outcomeKey) => {
      const dedupe = `${task.key}\u0001${outcomeKey}`;
      if (seen.has(dedupe)) return;
      seen.add(dedupe);
      next.push({
        key: `edge_${task.key}_${outcomeKey}_end`,
        fromTaskKey: task.key,
        outcomeKey,
        toTaskKey: null,
      });
    });
  });

  return normalizeWorkflowEdges(next);
}

/**
 * WorkflowDesignerCanvasInner
 *
 * @returns {ReactNode}
 */
const WorkflowDesignerCanvasInner = ({
  edges: templateEdges = [],
  fitNonce = 0,
  onAddOutcome,
  onChange,
  onConnectTasks,
  onEditTask,
  onSelectedOutcomeChange,
  tasks = [],
}) => {
  const { theme } = useTheme();
  const { fitView } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [flowEdges, setFlowEdges, onEdgesChange] = useEdgesState([]);
  const pendingFitRef = useRef(false);

  const handlers = useMemo(() => ({
    onAddOutcome,
    onEditTask,
  }), [onAddOutcome, onEditTask]);

  const graphSignature = useMemo(() => JSON.stringify({
    edges: normalizeWorkflowEdges(templateEdges).map((edge) => ([
      edge.fromTaskKey,
      edge.outcomeKey,
      edge.toTaskKey,
    ])),
    tasks: (Array.isArray(tasks) ? tasks : []).map((task) => ([
      task?.key,
      task?.title,
      task?.handledBy,
      (task?.outcomes || []).map((outcome) => [outcome?.key, outcome?.label]),
      (task?.assignees || []).map((person) => [
        person?.key || person?.userKey,
        person?.name || person?.data_profile?.name,
        person?.coverUrl,
      ]),
      task?.canvas?.x,
      task?.canvas?.y,
    ])),
  }), [tasks, templateEdges]);

  useEffect(() => {
    if (fitNonce > 0) {
      pendingFitRef.current = true;
    }
  }, [fitNonce]);

  useEffect(() => {
    setNodes(buildFlowNodes(tasks, templateEdges, handlers));
    setFlowEdges(buildFlowEdges(templateEdges, tasks));

    if (!pendingFitRef.current) return undefined;
    pendingFitRef.current = false;
    const frame = window.requestAnimationFrame(() => {
      fitView({ padding: 0.2, duration: 200 });
    });
    return () => window.cancelAnimationFrame(frame);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphSignature, handlers]);

  const validation = useMemo(
    () => validateWorkflowGraph(tasks, templateEdges),
    [tasks, templateEdges],
  );

  const emitGraph = useCallback((nextNodes, nextFlowEdges, previousEdges = templateEdges) => {
    const nextTasks = (Array.isArray(tasks) ? tasks : []).map((task) => {
      const node = (Array.isArray(nextNodes) ? nextNodes : []).find((row) => row.id === task.key);
      if (!node) return task;
      return {
        ...task,
        canvas: {
          x: Number(node.position?.x) || 0,
          y: Number(node.position?.y) || 0,
        },
      };
    });
    const nextEdges = flowEdgesToWorkflowEdges(nextFlowEdges, previousEdges, nextTasks);
    onChange?.({ tasks: nextTasks, edges: nextEdges });
  }, [onChange, tasks, templateEdges]);

  const handleNodeDragStop = useCallback((_event, _node, nextNodes) => {
    emitGraph(nextNodes || nodes, flowEdges);
  }, [emitGraph, flowEdges, nodes]);

  const handleConnect = useCallback((connection) => {
    const source = String(connection?.source || '').trim();
    const target = String(connection?.target || '').trim();
    if (!source || !target || source === target) return;
    onConnectTasks?.({ fromTaskKey: source, toTaskKey: target });
  }, [onConnectTasks]);

  const handleEdgesDelete = useCallback((deleted) => {
    const deletedList = Array.isArray(deleted) ? deleted : [];
    // Never remove Complete connectors — keep them in the canvas and graph.
    const removableIds = new Set(
      deletedList
        .filter((edge) => getFlowEdgeOutcomeKey(edge) !== WORKFLOW_OUTCOME_COMPLETE)
        .map((edge) => edge.id),
    );
    if (removableIds.size === 0) return;

    setFlowEdges((current) => {
      const next = current.filter((edge) => !removableIds.has(edge.id));
      emitGraph(nodes, next, templateEdges);
      return next;
    });
    onSelectedOutcomeChange?.(null);
  }, [emitGraph, nodes, onSelectedOutcomeChange, setFlowEdges, templateEdges]);

  const handleNodesDelete = useCallback((deleted) => {
    const deletedKeys = (Array.isArray(deleted) ? deleted : [])
      .map((node) => String(node?.id || '').trim())
      .filter(Boolean);
    if (deletedKeys.length === 0) return;

    let nextTasks = Array.isArray(tasks) ? tasks : [];
    let nextEdges = templateEdges;
    deletedKeys.forEach((taskKey) => {
      const result = removeWorkflowTaskFromGraph(nextTasks, nextEdges, taskKey);
      nextTasks = result.tasks;
      nextEdges = result.edges;
    });

    onSelectedOutcomeChange?.(null);
    onChange?.({ tasks: nextTasks, edges: nextEdges });
  }, [onChange, onSelectedOutcomeChange, tasks, templateEdges]);

  const handleSelectionChange = useCallback(({ edges: selectedEdges } = {}) => {
    const selected = Array.isArray(selectedEdges) ? selectedEdges : [];
    if (selected.length !== 1) {
      onSelectedOutcomeChange?.(null);
      return;
    }

    const edge = selected[0];
    const outcomeKey = getFlowEdgeOutcomeKey(edge);
    if (!outcomeKey || outcomeKey === WORKFLOW_OUTCOME_COMPLETE) {
      onSelectedOutcomeChange?.(null);
      return;
    }

    const fromTaskKey = String(edge?.source || '').trim();
    if (!fromTaskKey) {
      onSelectedOutcomeChange?.(null);
      return;
    }

    const fromTask = (Array.isArray(tasks) ? tasks : []).find((task) => task.key === fromTaskKey) || null;
    const label = getOutcomeLabel(fromTask, outcomeKey);

    onSelectedOutcomeChange?.({
      fromTaskKey,
      label,
      outcomeKey,
    });
  }, [onSelectedOutcomeChange, tasks]);

  const handleAutoLayout = useCallback(() => {
    const order = buildTopologicalTaskKeys(tasks, templateEdges);
    const orderedTasks = order
      .map((key) => tasks.find((task) => task.key === key))
      .filter(Boolean);
    const leftovers = tasks.filter((task) => !order.includes(task.key));
    const nextTasks = assignWorkflowSpineCanvas([...orderedTasks, ...leftovers], {
      force: true,
      gapX: 280,
      startX: 80,
      y: 140,
    });
    onChange?.({ tasks: nextTasks, edges: templateEdges });
    window.requestAnimationFrame(() => fitView({ padding: 0.2, duration: 200 }));
  }, [fitView, onChange, tasks, templateEdges]);

  const handleFitView = useCallback(() => {
    fitView({ padding: 0.2, duration: 200 });
  }, [fitView]);

  return (
    <div className="workflow-designer-canvas">
      <div className="workflow-designer-canvas-toolbar">
        <Button
          backgroundColor="transparent"
          fontSize="12px"
          foregroundColor={theme.foregroundMuted}
          height="32px"
          iconKey="account_tree"
          label="Auto-layout"
          padding="0 10px"
          onClick={handleAutoLayout}
        />
        <Button
          backgroundColor="transparent"
          fontSize="12px"
          foregroundColor={theme.foregroundMuted}
          height="32px"
          iconKey="fit_screen"
          label="Fit"
          padding="0 10px"
          onClick={handleFitView}
        />
      </div>

      {!validation.ok ? (
        <div
          className="workflow-designer-canvas-errors"
          style={{
            backgroundColor: theme.backgroundMuted,
            borderColor: theme.outline,
            color: theme.foreground,
          }}
        >
          {validation.errors.slice(0, 4).map((error) => (
            <div key={error}>{error}</div>
          ))}
        </div>
      ) : null}

      <ReactFlow
        deleteKeyCode={['Backspace', 'Delete']}
        edges={flowEdges}
        fitView
        nodes={nodes}
        nodeTypes={nodeTypes}
        onConnect={handleConnect}
        onEdgesChange={onEdgesChange}
        onEdgesDelete={handleEdgesDelete}
        onNodesChange={onNodesChange}
        onNodesDelete={handleNodesDelete}
        onNodeDragStop={handleNodeDragStop}
        onSelectionChange={handleSelectionChange}
        proOptions={{ hideAttribution: true }}
      >
        <Background color={theme.outline} gap={18} size={1} />
        <Controls />
        <MiniMap
          maskColor="transparent"
          nodeColor={() => theme.blue}
          style={{ backgroundColor: theme.backgroundMuted }}
        />
      </ReactFlow>
    </div>
  );
};

/**
 * WorkflowDesignerCanvas
 *
 * @returns {ReactNode}
 */
const WorkflowDesignerCanvas = (props) => (
  <ReactFlowProvider>
    <WorkflowDesignerCanvasInner {...props} />
  </ReactFlowProvider>
);

WorkflowDesignerCanvas.displayName = 'WorkflowDesignerCanvas';

export default WorkflowDesignerCanvas;
